import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button, InputText, Toast, DataTable, Column, ConfirmDialog, Dialog } from 'primereact';
import api from '../../services/api';
import MetaTagsLiveCodePreview from '../EntryPages/EntryPage/MetaTagsLiveCodePreview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import { tagOptions } from '../../constants/constants'
import { Timeline } from 'primereact/timeline';
import {
    APP_ROUTER_PATH_LIST,
    normalizePath,
    revalidatePublicPathsInBatches,
} from '../../utils/revalidatePublic';

const REVALIDATE_BATCH_SIZE = 5;

function slugListFromSummary(data) {
    return (Array.isArray(data) ? data : [])
        .map((page) => normalizePath(page?.full_slug))
        .filter(Boolean);
}

const MetaTagsManager = () => {

    const dialogStyle = {
        width: '50vw',
        overflow: 'visible'
    };

    const [tags, setTags] = useState([{ key: '', value: '' }]);
    const [metaTags, setMetaTags] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedMetaTag, setSelectedMetaTag] = useState(null);
    const [visibleDialog, setVisibleDialog] = useState(false);
    const [currentContent, setCurrentContent] = useState('');
    const [selectedType, setSelectedType] = useState({});

    const toast = useRef(null);

    const showToast = useCallback((severity, summary, detail) => {
        toast.current.show({ severity, summary, detail });
    }, []);

    const optionTemplate = (option) => {
        return (
            <div className={`flex align-items-center gap-1 p-1 border-round`}>
                <div className="flex flex-column">
                    <span className="text">{option.label}</span>
                </div>
            </div>
        );
    };

    const selectedOptionTemplate = (option) => {
        if (!option) return "Select tag type";

        return (
            <div className="flex align-items-center gap-2">
                <span className="font-medium">{option.label}</span>
            </div>
        );
    };

    // Fetch existing meta tags
    const fetchMetaTags = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/meta_tags");
            // console.log('Fetched meta tags:', data);
            setMetaTags(data);
        } catch (error) {
            showToast('error', 'Error', 'Could not load meta tags');
        }
        setLoading(false);
    }, [showToast]);

    useEffect(() => {
        fetchMetaTags();
    }, [fetchMetaTags]);

    const metaTagsToPreview = useMemo(() => {
        console.log('tags', tags)
        const attributesObject = tags.reduce((acc, tag) => {
            if (tag.key.trim()) acc[tag.key.trim()] = tag.value.trim();
            return acc;
        }, {});

        // console.log('attributesObject', attributesObject)

        return [{
            // name: currentName,
            content: currentContent,
            self_closing: selectedType?.self_closing || false,
            type: selectedType?.code,
            attributes: attributesObject,
        }]
    }, [tags, currentContent, selectedType]);

    // console.log('metaTagsToPreview', metaTagsToPreview);

    const validAttributes = [
        'name', 'content', 'property', 'charset', 'http-equiv',
        'rel', 'href', 'src', 'alt', 'title', 'type', 'sizes'
    ].map(attr => ({ label: attr, value: attr }));

    const handleInputChange = (index, field, value, maxLength = 50) => {
        const newTags = [...tags];

        if (field === 'key') {
            // 1. Eliminar espacios
            let cleanedValue = value.replace(/\s+/g, '');

            // 2. Limitar longitud máxima
            // const validChars = /^[a-zA-Z0-9_-]*$/;
            if (cleanedValue.length > maxLength) {
                cleanedValue = cleanedValue.substring(0, maxLength);
            }
            // 3. Opcional: Validar caracteres permitidos (solo letras, números y guiones)
            newTags[index][field] = cleanedValue;
        } else {
            newTags[index][field] = value;
        }

        setTags(newTags);
    };
    const addTagField = () => {
        setTags([...tags, { key: '', value: '' }]);
    };

    const removeTagField = (index) => {
        console.log('Removing tag at index:', index);
        const newTags = tags.filter((_, i) => i !== index);
        setTags(newTags);
    };

    // API operations
    const createMetaTag = async () => {
        try {

            if (!selectedType?.code) {
                showToast('error', 'Error', 'Please select a tag type');
                return;
            }
            // if (!currentName) {
            //     showToast('error', 'Error', 'Please enter a name for the meta tag');
            //     return;
            // }

            // Filter out tags with empty key or value
            const filteredTags = tags.filter(tag => tag.key.trim() && tag.value.trim());
            if (selectedType.self_closing && filteredTags.length === 0) {
                showToast('error', 'Error', 'Please add at least one tag with both key and value');
                return;
            }
            const tagObject = filteredTags.reduce((acc, tag) => {
                acc[tag.key.trim()] = tag.value.trim();
                return acc;
            }, {});

            const { data } = await api.post("/meta_tags", {
                attributes: tagObject,
                type: selectedType?.code,
                content: currentContent || '',
                self_closing: selectedType?.self_closing || false,
                // name: currentName || `MetaTag-${Date.now()}`
            });

            setMetaTags([...metaTags, data]);
            setTags([{ key: '', value: '' }]);
            // setCurrentName('');
            setSelectedType(null);
            setVisibleDialog(false);
            showToast('success', 'Success', 'Meta tag created successfully');
        } catch (err) {
            showToast('error', 'Error', 'Failed to create meta tag');
        }
    };

    const updateMetaTag = async (id, updatedData) => {
        try {
            setLoading(true);

            await api.put(`/meta_tags/${id}`, updatedData).then(({ data }) => {
                fetchMetaTags();
                showToast('success', 'Success', 'Meta tag updated successfully');
                setVisibleDialog(false);
            }).catch(err => {
                showToast('error', 'Error', 'Failed to update meta tag');
            }).finally(() => {
                setLoading(false);
            });
        } catch (err) {
            showToast('error', 'Error', 'Failed to update meta tag');
        }
    };

    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [metaTagToDelete, setMetaTagToDelete] = useState(null);

    const handleDelete = async () => {
        try {
            await api.delete(`/meta_tags/${metaTagToDelete}`);
            setMetaTags(metaTags.filter(tag => tag._id !== metaTagToDelete));
            showToast('success', 'Success', 'Meta tag deleted successfully');
        } catch (err) {
            showToast('error', 'Error', 'Failed to delete meta tag');
        }
        setDeleteDialogVisible(false);
    };

    const confirmDelete = (id) => {
        setMetaTagToDelete(id);
        setDeleteDialogVisible(true);
    };

    const openNewMetaTagDialog = () => {
        setSelectedMetaTag(null);
        setSelectedType({});
        // setCurrentName('');
        setCurrentContent('');
        setTags([{ key: '', value: '' }]);
        setVisibleDialog(true);
    };

    const tagsBodyTemplate = (rowData) => {
        return (
            <div className="flex flex-column gap-2">
                {Object.entries(rowData.attributes || {}).map(([key, value]) => (
                    <div key={key} className="flex align-items-center gap-2">
                        <span className="font-bold">{key}:</span>
                        <span>{value}</span>
                    </div>
                ))}
            </div>
        );
    }
        ;
    const contentBodyTemplate = (rowData) => {
        return (
            <div className="flex flex-column gap-2">
                <div className="flex align-items-center gap-2">
                    {/* <span>{rowData.content || 'Empty'}</span> */}
                    {
                        rowData.self_closing ? (
                            <span className="">(self-closing/no apply)</span>
                        ) : <span>
                            {rowData.content || 'Empty'}
                        </span>
                    }
                </div>
            </div>
        );
    };

    const tagBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center">
                <span>{`<${rowData.type || 'Unnamed Meta Tag'}>`}</span>
            </div>
        );
    };

    const dateBodyTemplate = (date) => {
        return new Date(date).toLocaleString();
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-info p-button-sm"
                    onClick={() => {
                        setSelectedMetaTag(rowData);
                        setTags(Object.entries(rowData.attributes || {}).map(([key, value]) => ({ key, value })));
                        // setCurrentName(rowData.name);
                        setCurrentContent(rowData.content);

                        const type = tagOptions.find(opt => opt.code === rowData.type);
                        setSelectedType(type);

                        setVisibleDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-danger p-button-sm"
                    onClick={(e) => {
                        e.stopPropagation(); // Previene eventos no deseados
                        confirmDelete(rowData._id);
                    }}
                />
            </div>
        );
    };

    const [events, setEvents] = useState([]);

    const [running, setRunning] = useState(false);
    const [visiblePublishModal, setVisiblePublishModal] = useState(false);
    const [publishStats, setPublishStats] = useState({ ok: 0, failed: 0 });
    const [activePath, setActivePath] = useState('');
    const [loadingPages, setLoadingPages] = useState(true);
    const runningRef = useRef(false);
    const timelineScrollRef = useRef(null);

    const startPreProcess = useCallback(async () => {
        setLoadingPages(true);
        setPublishStats({ ok: 0, failed: 0 });
        setActivePath('');

        const settled = await Promise.allSettled([
            api.get('/entry_pages/summary'),
            api.get('/country_pages/summary'),
            api.get('/blogs_pages/summary'),
        ]);

        const [entryRes, countryRes, blogRes] = settled;
        const entrySlugs =
            entryRes.status === 'fulfilled' ? slugListFromSummary(entryRes.value.data) : [];
        const countrySlugs =
            countryRes.status === 'fulfilled' ? slugListFromSummary(countryRes.value.data) : [];
        const blogSlugs =
            blogRes.status === 'fulfilled' ? slugListFromSummary(blogRes.value.data) : [];

        settled.forEach((result, index) => {
            if (result.status === 'rejected') {
                const labels = ['entry_pages', 'country_pages', 'blogs_pages'];
                console.warn(`Could not load ${labels[index]} for revalidate`, result.reason);
            }
        });

        const grouped = [
            ...APP_ROUTER_PATH_LIST.map((full_slug) => ({
                full_slug,
                status: full_slug,
                group: 'App',
                color: '#3B82F6',
            })),
            ...entrySlugs.map((full_slug) => ({
                full_slug,
                status: full_slug,
                group: 'Entry',
                color: '#27b54bff',
            })),
            ...countrySlugs.map((full_slug) => ({
                full_slug,
                status: full_slug,
                group: 'Country',
                color: '#8B5CF6',
            })),
            ...blogSlugs.map((full_slug) => ({
                full_slug,
                status: full_slug,
                group: 'Blog',
                color: '#F59E0B',
            })),
        ];

        const seen = new Set();
        const uniqueEvents = [];
        for (const event of grouped) {
            if (!event.full_slug || seen.has(event.full_slug)) continue;
            seen.add(event.full_slug);
            uniqueEvents.push({
                ...event,
                completed: false,
                failed: false,
                inProgress: false,
            });
        }

        setEvents(uniqueEvents);
        setLoadingPages(false);
    }, []);

    const publishedCount = events.filter((event) => event.completed).length;

    const startProcess = useCallback(async () => {
        const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
        const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';

        if (!publicUrl || !secret) {
            showToast(
                'warn',
                'Missing config',
                'Set REACT_APP_BORDERGARITA_PUBLIC_APP_URL and REVALIDATE_SECRET'
            );
            return;
        }

        if (!events.length || runningRef.current) return;

        runningRef.current = true;
        setRunning(true);
        setPublishStats({ ok: 0, failed: 0 });
        setActivePath('');
        setEvents((prev) =>
            prev.map((event) => ({
                ...event,
                completed: false,
                failed: false,
                inProgress: false,
            }))
        );

        try {
            const paths = events.map((event) => event.full_slug).filter(Boolean);
            const result = await revalidatePublicPathsInBatches(publicUrl, secret, paths, {
                batchSize: REVALIDATE_BATCH_SIZE,
                onBatchStart: ({ paths: batchPaths }) => {
                    const batchSet = new Set(batchPaths);
                    setEvents((prev) =>
                        prev.map((event) => ({
                            ...event,
                            inProgress: !event.completed && batchSet.has(event.full_slug),
                        }))
                    );
                    const firstPath = batchPaths[0];
                    if (firstPath) {
                        setActivePath(firstPath);
                        requestAnimationFrame(() => {
                            const row = timelineScrollRef.current?.querySelector(
                                `[data-revalidate-path="${CSS.escape(firstPath)}"]`
                            );
                            row?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest',
                            });
                        });
                    }
                },
                onPathDone: ({ path, ok }) => {
                    setActivePath(path);
                    setEvents((prev) =>
                        prev.map((event) =>
                            event.full_slug === path
                                ? {
                                    ...event,
                                    completed: true,
                                    failed: !ok,
                                    inProgress: false,
                                }
                                : event
                        )
                    );
                    setPublishStats((prev) => ({
                        ok: prev.ok + (ok ? 1 : 0),
                        failed: prev.failed + (ok ? 0 : 1),
                    }));

                    requestAnimationFrame(() => {
                        const row = timelineScrollRef.current?.querySelector(
                            `[data-revalidate-path="${CSS.escape(path)}"]`
                        );
                        row?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    });
                },
            });

            setActivePath('');
            showToast(
                result.failed ? 'warn' : 'success',
                'Revalidation Complete',
                result.failed
                    ? `Revalidated ${result.ok}/${result.total} paths (${result.failed} failed)`
                    : `Revalidated ${result.ok} paths across the full site`
            );
        } catch (err) {
            console.error(err);
            setActivePath('');
            showToast('error', 'Publish error', err?.message || 'Revalidation failed');
        } finally {
            runningRef.current = false;
            setRunning(false);
        }
    }, [events, showToast]);

    return (
        <div className="card p-4">
            <Toast ref={toast} />
            <ConfirmDialog

                style={{
                    zIndex: 9999,
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                }}
            />

            <div className="flex justify-content-between align-items-center mb-4">
                <h2>Global SEO</h2>
                <div className='gap-2 flex'>
                    <Button
                        label="New Tag"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={openNewMetaTagDialog}
                    />
                    <Button
                        label="Publish Meta Tags"
                        icon="pi pi-refresh"
                        className="p-button-success"
                        onClick={() => {
                            setEvents([]);
                            setPublishStats({ ok: 0, failed: 0 });
                            setActivePath('');
                            setVisiblePublishModal(true);
                            setLoadingPages(true);
                            startPreProcess();
                        }}
                    />
                </div>
            </div>

            {/* Meta Tags Table */}
            <div className="card">
                {
                    loading && (
                        <div className="row text-center" style={{ height: '100px' }}>
                            <div>
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
                            </div>
                            <div className='block mt-2'>
                                Loading meta tags
                            </div>
                        </div>
                    )
                }
                <DataTable
                    value={metaTags}
                    selectionMode="single"
                    dataKey="_id"
                    emptyMessage="No meta tags found"
                >
                    <Column field="type" header="Type" body={tagBodyTemplate} />
                    <Column field="attributes" header="Propierties" body={tagsBodyTemplate} />
                    <Column field="content" header="Content" body={contentBodyTemplate} />
                    <Column field="created_at" header="Created" body={(row) => dateBodyTemplate(row.created_at)} sortable />
                    <Column field="updated_at" header="Updated" body={(row) => dateBodyTemplate(row.updated_at)} sortable />
                    <Column body={actionBodyTemplate} style={{ width: '120px', textAlign: 'center' }} />
                </DataTable>
            </div>
            <Dialog
                modal
                style={{ ...dialogStyle, width: '42vw' }}
                visible={visiblePublishModal}
                onHide={() => {
                    if (running) return;
                    setVisiblePublishModal(false);
                }}
                header="Publish global SEO"
                footer={
                    <div className="flex justify-content-end">
                        <Button
                            onClick={startProcess}
                            disabled={running || loadingPages || events.length === 0}
                            className="gap-2"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.65rem' }}
                        >
                            {running ? (
                                <i
                                    className="pi pi-spin pi-spinner"
                                    style={{ fontSize: '0.95rem', marginRight: '0.15rem' }}
                                    aria-hidden
                                />
                            ) : null}
                            <span>{running ? 'Revalidating' : 'Revalidate full site'}</span>
                        </Button>
                    </div>
                }
            >
                <div className="card">
                    <div>
                        <p className="text-sm text-color-secondary mt-2 mb-0">
                            Revalidates App Router pages (homes, blog listing, legal, sitemap),
                            entry pages, country pages, and blogs in batches of {REVALIDATE_BATCH_SIZE}.
                        </p>
                    </div>
                    {loadingPages ? (
                        <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '100px' }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                            <span className="mt-2">Loading all site paths...</span>
                        </div>
                    ) : (
                        <div className="mt-3 mb-2">
                            <h4 className="mt-0 mb-0 flex align-items-center" style={{ gap: '0.55rem' }}>
                                {running ? (
                                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                                ) : null}
                                <span>
                                    {publishedCount}/{events.length} paths revalidated
                                    {publishStats.failed > 0
                                        ? ` · ${publishStats.failed} failed`
                                        : ''}
                                    .
                                </span>
                            </h4>
                            {activePath ? (
                                <div className="text-sm text-color-secondary mt-2">
                                    Current: {activePath}
                                </div>
                            ) : null}
                        </div>
                    )}
                    <div
                        ref={timelineScrollRef}
                        style={{
                            maxHeight: '52vh',
                            overflowY: 'auto',
                            scrollBehavior: 'smooth',
                            paddingRight: '0.5rem',
                            marginTop: '0.75rem',
                        }}
                    >
                        <Timeline
                            value={events}
                            content={(item) => {
                                return (
                                    <div
                                        data-revalidate-path={item.full_slug}
                                        style={{
                                            padding: '0.35rem 0.5rem',
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: '#111827',
                                                fontWeight: 400,
                                                width: '100%',
                                                display: 'inline-block',
                                            }}
                                        >
                                            <span className="text-xs mr-2 text-color-secondary">
                                                [{item.group}]
                                            </span>
                                            {item.status}
                                        </span>
                                    </div>
                                );
                            }}
                            marker={(item) => {
                                if (item.inProgress) {
                                    return (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '22px',
                                                height: '22px',
                                            }}
                                            aria-label="Revalidating"
                                        >
                                            <i
                                                className="pi pi-spin pi-spinner"
                                                style={{ fontSize: '0.95rem', color: '#3B82F6' }}
                                            />
                                        </span>
                                    );
                                }

                                if (item.completed && !item.failed) {
                                    return (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '22px',
                                                height: '22px',
                                                borderRadius: '999px',
                                                background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
                                            }}
                                            aria-label="Revalidated"
                                        >
                                            <i
                                                className="pi pi-check"
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </span>
                                    );
                                }

                                if (item.failed) {
                                    return (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '22px',
                                                height: '22px',
                                                borderRadius: '999px',
                                                background: '#dc2626',
                                            }}
                                            aria-label="Failed"
                                        >
                                            <i
                                                className="pi pi-times"
                                                style={{ fontSize: '0.7rem', color: '#fff' }}
                                            />
                                        </span>
                                    );
                                }

                                return (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '999px',
                                            border: `2px solid ${item.color || '#94a3b8'}`,
                                            background: 'transparent',
                                        }}
                                        aria-label="Pending"
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                visible={deleteDialogVisible}
                style={dialogStyle}
                header="Confirm Tag Delete"
                modal
                footer={
                    <div>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            onClick={() => setDeleteDialogVisible(false)}
                            className="p-button-text"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            onClick={handleDelete}
                            className="p-button-danger"
                            autoFocus
                        />
                    </div>
                }
                onHide={() => setDeleteDialogVisible(false)}
            >
                <div className="flex align-items-center justify-content-center">
                    {/* <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} /> */}
                    <span>Are you sure you want to delete this meta tag?</span>
                </div>
            </Dialog>
            <Dialog
                header={selectedMetaTag ? 'Edit Tag' : 'New Tag'}
                visible={visibleDialog}
                style={{ width: '50vw', maxHeight: '80vh' }}
                onHide={() => {
                    setVisibleDialog(false);
                    setSelectedMetaTag(null);
                }}
                contentStyle={{ overflowY: 'auto', maxHeight: '65vh' }}
            >
                <div className="p-fluid">
                    <h4>Tags</h4>
                    <div className="field mt-2">
                        <div className="grid">
                            <div className="col-6">
                                <label htmlFor="name">
                                    <strong>
                                        Tag type
                                    </strong>
                                </label>
                                <Dropdown
                                    value={selectedType}
                                    options={tagOptions}
                                    filterInputAutoFocus
                                    filter
                                    showClear
                                    filterBy="code"
                                    onChange={(e) => {
                                        setSelectedType(e.value)
                                    }}
                                    placeholder="Select an option"
                                    itemTemplate={optionTemplate}

                                    valueTemplate={selectedOptionTemplate}
                                    appendTo="self"
                                    panelStyle={{
                                        zIndex: 10000,
                                        position: 'relative'
                                    }}
                                />
                            </div>
                            {/* <div className="col-6">
                                <label htmlFor="name">
                                    <strong>
                                        Name
                                    </strong>
                                    <span className="ml-1 text-gray-300 text-sm">(for internal use)</span>
                                </label>
                                <InputText
                                    id="name"
                                    value={currentName}
                                    disabled={loading || !selectedType?.code}
                                    onChange={(e) => setCurrentName(e.target.value)}
                                    placeholder="Enter a name for this tag set"
                                />
                            </div> */}
                        </div>
                    </div>
                    {
                        !selectedType?.self_closing && (
                            <div className="field">
                                <label htmlFor="content">
                                    <strong>
                                        Content
                                    </strong>
                                </label>
                                <InputText
                                    id="content"
                                    value={currentContent}
                                    disabled={loading || !selectedType?.code}
                                    onChange={(e) => setCurrentContent(e.target.value)}
                                    placeholder={`Enter content for this ${selectedType?.code || 'tag'} tag`}
                                />
                            </div>
                        )
                    }

                    <div className="space-y-4 mt-3">
                        <label htmlFor="content">
                            <strong>
                                Properties
                            </strong>
                        </label>
                        {tags.map((tag, index) => (
                            <div key={index} className="flex gap-2 items-center mt-2">
                                {/* <InputText
                                    value={tag.key}
                                    disabled={loading || !selectedType?.code}
                                    placeholder="Key (letters, numbers and hyphens only)"
                                    onChange={(e) => handleInputChange(index, 'key', e.target.value)}
                                    className="w-1/3"
                                /> */}
                                {/* validAttributes */}
                                <Dropdown
                                    value={tag.key}
                                    options={validAttributes}
                                    filterInputAutoFocus
                                    filter
                                    showClear
                                    filterBy="label"
                                    onChange={(e) => {
                                        handleInputChange(index, 'key', e.target.value)
                                        // setSelectedType(e.value)
                                    }}
                                    placeholder="Select an option"
                                    itemTemplate={optionTemplate}
                                    valueTemplate={selectedOptionTemplate}
                                    appendTo="self"
                                    className="w-1/3"
                                    panelStyle={{
                                        zIndex: 10000,
                                        position: 'relative'
                                    }}
                                />
                                <InputText
                                    value={tag.value}
                                    placeholder="Value"
                                    disabled={loading || !selectedType?.code}
                                    onChange={(e) => handleInputChange(index, 'value', e.target.value)}
                                    className="w-1/2"
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-danger p-button-sm"
                                    onClick={() => removeTagField(index)}
                                // disabled={tags.length <= 1 || loading}
                                />
                            </div>
                        ))}
                        {
                            tags.length === 0 && (
                                <div className="text-color-secondary text-sm mt-2">
                                    No properties for this tag type
                                </div>
                            )
                        }
                        <div className="flex justify-content-between align-items-center mt-4 pt-3 border-top-1 surface-border">
                            <div className="flex align-items-left">
                                <div className='flex'>
                                    <Button
                                        label="Add Field"
                                        icon="pi pi-plus"
                                        disabled={loading}
                                        className="p-button-outlined p-button-secondary mr-2"
                                        onClick={addTagField}
                                    />
                                    <span className="text-color-secondary text-sm">({tags.length} fields)</span>
                                </div>
                            </div>
                        </div>
                        <Accordion activeIndex={0} className='mt-4'>
                            <AccordionTab header="Meta Tags Properties">
                                <MetaTagsLiveCodePreview
                                    metaTags={metaTagsToPreview}
                                />
                            </AccordionTab>
                        </Accordion>
                        <div className="flex justify-content-between align-items-center mt-4 pt-3 border-top-1 surface-border">
                            <div className="flex align-items-center">
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    label={selectedMetaTag ? 'Update' : 'Create'}
                                    icon="pi pi-save"
                                    loading={loading}
                                    className="p-button-primary"
                                    onClick={async () => {
                                        if (selectedMetaTag) {
                                            const tagObject = tags.reduce((acc, tag) => {
                                                if (tag.key.trim()) acc[tag.key.trim()] = tag.value.trim();
                                                return acc;
                                            }, {});

                                            await updateMetaTag(selectedMetaTag._id, {
                                                // name: currentName,
                                                attributes: tagObject,
                                                content: currentContent,
                                                type: selectedType?.code,
                                                self_closing: selectedType?.self_closing || false
                                            });
                                        } else {
                                            await createMetaTag();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Live Preview */}
            <div className="mt-5">
                <h3>Preview</h3>
                <MetaTagsLiveCodePreview
                    metaTags={metaTags}
                />
            </div>
        </div>
    );
};

export default MetaTagsManager;